import DeletableText from "@/Components/DeletableText";
import Search from "@/Components/Input/Search";
import Tag from "@/Components/Tag/Tag";
import useIsTablePos from "@/Pages/Auth/Bestellingen/PosOrder/useIsTablePos";
import usePosOrderDeliveryMethod from "@/shared/atoms/usePosOrderDeliveryMethodAtom";
import usePosOrderMenuValues from "@/shared/atoms/usePosOrderMenuValuesAtom";
import {
  useSearchBarcode,
  useSearchText,
  useSetPosOrderSearch,
} from "@/shared/atoms/usePosOrderSearchAtom";
import usePosOrderTable, {
  useSetPosOrderTable,
} from "@/shared/atoms/usePosOrderTable";
import usePosShowOrders from "@/shared/atoms/usePosShowOrders";
import { useSetPosShowPayment } from "@/shared/atoms/usePosShowPayment";
import useIsMobileHook from "@/shared/hooks/useIsMobileHook";
import useIsWaiter from "@/shared/hooks/useIsWaiter";
import {
  useHasOrders,
  useHasSession,
  useSessionId,
} from "@/shared/hooks/usePosOrderTableSessionHook";
import usePosSettingsHook from "@/shared/hooks/usePosSettingsHook";
import useOrderSessionDeleteMutation from "@/shared/mutations/orderSessions/useOrderSessionDeleteMutation";
import { CategoryCardPositionEnum, DeliveryMethods } from "@/shared/utils/enum";
import { getFieldByLang } from "@/shared/utils/helpers";
import { useTranslation } from "react-i18next";
import UserHelper from "../Base/UserHelper";
import Icon from "../Icon/Icon";
import Select from "../Select/Select";
import PosButton from "./PosButton";
import usePosOrderStandardHook from "@/shared/hooks/usePosOrderStandardHook";
import { DeliveryMethodIdType } from "@/shared/utils/interfaces";
import { useSetCart } from "@/shared/hooks/useCartHook";
import { cn } from "@/shared/utils/cn";
import { useSetPosShowSettings } from "@/shared/atoms/usePosShowSettings";

const PosMenuTop = () => {
  const hasSession = useHasSession();
  const sessionId = useSessionId();
  const hasOrders = useHasOrders();
  const isWaiter = useIsWaiter();
  const isTables = useIsTablePos();
  const [table] = usePosOrderTable();
  const { t } = useTranslation();
  const isMobile = useIsMobileHook();
  const {
    settings: { category_card_position: cardPosition },
    posDeliveryMethods,
  } = usePosSettingsHook();
  const [{ selectedTab, selectedCat }, setPosOrderMenuValues] =
    usePosOrderMenuValues();
  const [showPosOrders, setPosOrders] = usePosShowOrders();
  const [deliveryMethod, setDeliveryMethod] = usePosOrderDeliveryMethod();
  const setSearch = useSetPosOrderSearch();
  const text = useSearchText();
  const barcode = useSearchBarcode();
  const destroy = useOrderSessionDeleteMutation();
  const setTable = useSetPosOrderTable();
  const setShowPayment = useSetPosShowPayment();
  const { setStandardPaymentMethod } = usePosOrderStandardHook();
  const setCart = useSetCart();
  const setShowSettings = useSetPosShowSettings();

  const onDeliveryMethodChange = (deliveryMethod: DeliveryMethodIdType) => {
    setShowPayment(false);
    setPosOrderMenuValues((draft) => {
      draft.selectedCat = null;
      draft.selectedTab = 0;
    });
    if (hasSession && !hasOrders) {
      setCart([]);
      destroy.mutateAsync([sessionId]);
    }
    setDeliveryMethod(deliveryMethod);
    setTable(null);

    if (deliveryMethod === DeliveryMethods.DINE_IN) {
      setPosOrders(false);
    }
    setStandardPaymentMethod(deliveryMethod);
  };

  const Wrapper = showPosOrders ? UserHelper : "div";

  return (
    <div className="flex-[0_0_auto]">
      <div className="flex flex-col md:flex-row flex-wrap justify-between mb-2 ">
        <div data-tour="pos-order.tabs" className="flex gap-1">
          {!isWaiter && (
            <PosButton
              onClick={() => {
                setPosOrderMenuValues((draft) => {
                  draft.selectedCat = null;
                  draft.selectedTab = 0;
                });
                setPosOrders(!showPosOrders);
              }}
              isActive={showPosOrders}
            >
              {t("orders")}
            </PosButton>
          )}
          {(!isTables || (isTables && table !== null)) &&
            cardPosition === CategoryCardPositionEnum.STANDARD && (
              <>
                <PosButton
                  onClick={() => {
                    setPosOrderMenuValues((draft) => {
                      draft.selectedCat = null;
                      draft.selectedTab = 0;
                    });
                    setPosOrders(false);
                  }}
                  isActive={selectedTab === 0 && !showPosOrders}
                >
                  {t("posOrders.categories")}
                </PosButton>
                <PosButton
                  onClick={() => {
                    setPosOrderMenuValues((draft) => {
                      draft.selectedTab = 1;
                    });
                    setPosOrders(false);
                  }}
                  isActive={selectedTab === 1 && !showPosOrders}
                >
                  {t("posOrders.products")}
                </PosButton>
              </>
            )}
          {!isWaiter && (
            <PosButton
              onClick={() => setShowSettings((prev) => !prev)}
              className="px-6 flex-0 md:hidden"
            >
              <Icon variants="gear" className="fill-primary size-8" />
            </PosButton>
          )}
        </div>
        <div
          data-tour="pos-order.delivery-methods"
          className={cn("hidden md:flex gap-1", isWaiter && "md:hidden")}
        >
          {posDeliveryMethods.map((method) => (
            <PosButton
              key={method.value}
              isActive={method.value === deliveryMethod}
              onClick={() => {
                onDeliveryMethodChange(method.value);
                setPosOrders(false);
              }}
            >
              {t(`${method.translationKey}`)}
            </PosButton>
          ))}
        </div>
        <div className={cn("block md:hidden mt-4", isWaiter && "hidden")}>
          <Select
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => t(`${option.translationKey}`)}
            value={posDeliveryMethods.find((o) => o.value === deliveryMethod)}
            onChange={(value) => onDeliveryMethodChange(value.value)}
            options={posDeliveryMethods}
            isSearchable={false}
          />
        </div>
      </div>
      {(!isTables || table) && !isWaiter && (
        <div
          data-tour="pos-order.search"
          className="flex flex-row  md:gap-0 mt-2 md:mt-4"
        >
          <div className="w-full pr-2">
            <Wrapper>
              <Search
                onDeleteClick={() =>
                  setSearch((draft) => {
                    draft.text = "";
                  })
                }
                value={text}
                onChange={(e) =>
                  setSearch((draft) => {
                    draft.text = e.target.value;
                  })
                }
                placeholder={
                  showPosOrders
                    ? t("orders_live.search")
                    : selectedCat
                      ? t("posOrders.searchInCategory", {
                          category: getFieldByLang(selectedCat.name),
                        })
                      : selectedTab === 0 &&
                          cardPosition === CategoryCardPositionEnum.STANDARD
                        ? t("posOrders.searchInCategories")
                        : t("posOrders.searchInProducts")
                }
              />
            </Wrapper>
          </div>
          {!showPosOrders && (
            <div className="hidden lg:block w-full">
              <Search
                onDeleteClick={() =>
                  setSearch((draft) => {
                    draft.barcode = "";
                  })
                }
                value={barcode}
                onChange={(e) =>
                  setSearch((draft) => {
                    draft.barcode = e.target.value;
                  })
                }
                placeholder={t("scanBarcodeToAddCart")}
                id="barcode"
                isRightAddonClickable
                onClickRightAddon={() =>
                  document.getElementById("barcode").focus()
                }
                inputRightAddon={
                  <Icon
                    className="h-[1.8rem] w-[1.8rem] fill-primary"
                    variants="barcode"
                  />
                }
              />
            </div>
          )}
        </div>
      )}
      {(isMobile || cardPosition === CategoryCardPositionEnum.STANDARD) &&
        selectedCat && (
          <div
            onClick={() =>
              setPosOrderMenuValues((draft) => {
                draft.selectedTab = 0;
                draft.selectedCat = null;
              })
            }
            className="cursor-pointer mt-[1rem]"
          >
            <Tag>
              <DeletableText
                className="font-bold"
                text={getFieldByLang(selectedCat.name)}
                deleteText={() =>
                  setPosOrderMenuValues((draft) => {
                    draft.selectedTab = 0;
                    draft.selectedCat = null;
                  })
                }
              />
            </Tag>
          </div>
        )}
    </div>
  );
};

export default PosMenuTop;
